import { ReactNode, useState } from 'react';
import { X, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LocalStorageExtension } from '@utils/extensions/storage.extension.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context';

interface Props {
  /**
   * localStorage key used to persist dismissal. Name-space it per feature,
   * e.g. `onboarding.tags-page.dismissed`.
   */
  storageKey: string;
  title: string;
  steps: ReactNode[];
  /** Optional custom label for the icon (defaults to a lightbulb). */
  icon?: ReactNode;
}

/**
 * Dismissible "how to use this" callout. Reads/writes a boolean flag to
 * localStorage under `storageKey` so it disappears permanently once the
 * user closes it. Renders nothing if already dismissed.
 */
export default function OnboardingCallout({
  storageKey,
  title,
  steps,
  icon
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [dismissed, setDismissed] = useState<boolean>(
    () => LocalStorageExtension.get(storageKey) === true
  );

  if (dismissed) return null;

  const handleDismiss = () => {
    LocalStorageExtension.set(storageKey, 1);
    setDismissed(true);
  };

  return (
    <div
      className="rounded-2xl border px-4 py-3 flex items-start gap-3"
      style={{
        borderColor: getColour('border'),
        backgroundColor: getColour('appSoft')
      }}
    >
      <div
        className="flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center"
        style={{ backgroundColor: getColour('secondLayer') }}
      >
        {icon ?? (
          <Lightbulb
            className="h-3.5 w-3.5"
            style={{ color: getColour('activeText') }}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-medium mb-1.5"
          style={{ color: getColour('normalText') }}
        >
          {title}
        </div>
        <ol
          className="text-xs space-y-0.5 list-decimal list-inside"
          style={{ color: getColour('inactiveText') }}
        >
          {steps.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ol>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="flex-shrink-0 p-1 rounded-md hover:bg-primary/5 transition-colors"
        aria-label={t('dismiss') || 'Dismiss'}
        title={t('dismiss') || 'Dismiss'}
      >
        <X
          className="h-3.5 w-3.5"
          style={{ color: getColour('inactiveText') }}
        />
      </button>
    </div>
  );
}
